'use client'

import { ArrowUpIcon, PlusIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { countAccountsAgainstPlanCap } from '@/features/account-access/lib/roles'
import { AccountConnectionSheet } from '@/features/dashboard/components/account-connection/account-connection-sheet'
import { useCapabilities } from '@/hooks/use-capabilities'
import { BILLING_ROUTE } from '@/lib/billing/routes'
import { cn } from '@/lib/utils'
import { useUserAccounts } from '@/service/account.hook'
import { AccountStatus } from '@/types/generated/common/account'

/**
 * The "add an account" action: opens the connection sheet, or — once the plan's account cap
 * is reached — sends the user to billing instead. Render `sheet` next to the trigger.
 */
export function useAddAccount() {
  const router = useRouter()
  const { maxAccounts } = useCapabilities()
  const { data } = useUserAccounts()
  const [sheetOpen, setSheetOpen] = useState(false)
  const atAccountCap = countAccountsAgainstPlanCap(data?.accounts ?? []) >= maxAccounts

  const addAccount = () => {
    if (atAccountCap) {
      router.push(BILLING_ROUTE)
      return
    }
    setSheetOpen(true)
  }

  return {
    atAccountCap,
    addAccount,
    sheet: <AccountConnectionSheet open={sheetOpen} onOpenChange={setSheetOpen} />,
  }
}

/**
 * True once accounts have loaded and none is active. Still loading counts as "has one", so
 * a widget's real content isn't briefly replaced by the call to action.
 */
export function useHasNoActiveAccount(): boolean {
  const { data } = useUserAccounts()
  const accounts = data?.accounts
  return (
    accounts !== undefined &&
    !accounts.some((account) => account.status === AccountStatus.ACCOUNT_STATUS_ACTIVE)
  )
}

interface NoAccountCtaProps {
  title: string
  hint: string
  className?: string
}

/**
 * What an account-scoped widget (positions, balances) shows instead of an empty table when
 * the user has no account yet: why it's empty, and the button to fix it.
 */
export function NoAccountCta({ title, hint, className }: NoAccountCtaProps) {
  const t = useTranslations('accountSelector')
  const { atAccountCap, addAccount, sheet } = useAddAccount()

  return (
    // m-auto rather than justify-center: centred when there's room, and scrollable from the top
    // (never clipped) in a widget shorter than the message.
    <div className={cn('flex h-full min-h-0 w-full overflow-y-auto p-3', className)}>
      <div
        data-tour="add-account"
        className="m-auto flex flex-col items-center gap-2.5 text-center"
      >
        <div className="flex flex-col gap-1">
          <h3 className="text-foreground/80 text-sm font-semibold">{title}</h3>
          <p className="text-foreground/50 text-xs leading-relaxed">{hint}</p>
        </div>
        <Button variant="cta" size="sm" onClick={addAccount}>
          {atAccountCap ? <ArrowUpIcon className="size-4" /> : <PlusIcon className="size-4" />}
          {atAccountCap ? t('accountLimitReachedUpgrade') : t('addNewAccount')}
        </Button>
        {sheet}
      </div>
    </div>
  )
}
